import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Divider,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

import AppModal from "../common/AppModal";
import FormSelectField from "../common/FormSelectField";
import FormTextField from "../common/FormTextField";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  evaluateTransitions,
  executeBulkTransitions,
} from "../../redux/actions/transitionRecordActions";

import { TransitionStatus } from "../../shared/enums/TransitionStatus";
import { TransitionRecordEvaluationRequestDto } from "../../shared/dtos/transitionRecord/TransitionRecordEvaluationRequestDto";
import { TransitionRecordBulkRequestDto } from "../../shared/dtos/transitionRecord/TransitionRecordBulkRequestDto";
import { TransitionRecordEvaluationResponseDto } from "../../shared/dtos/transitionRecord/TransitionRecordEvaluationResponseDto";

// Extending the DTO locally for the UI state
interface LocalEvaluation extends TransitionRecordEvaluationResponseDto {
  selectedClassroomId?: string | null;
}

const setupSchema = Yup.object().shape({
  fromAcademicYearId: Yup.string().required("From Year is required"),
  toAcademicYearId: Yup.string().required("To Year is required"),
  classroomId: Yup.string().required("Classroom is required"),
  passMark: Yup.number().required("Pass mark is required").min(0),
});

interface WizardFormValues {
  fromAcademicYearId: string;
  toAcademicYearId: string;
  classroomId: string;
  passMark: number;
  requireAllThreeTerms: boolean;
  isTerminalLevel: boolean;
}

interface TransitionWizardModalProps {
  open: boolean;
  onClose: () => void;
}

const TransitionWizardModal: React.FC<TransitionWizardModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  // --- Redux State ---
  const {
    evaluations,
    loadingEvaluation,
    loadingTransitionExecute,
    errorEvaluation,
    errorExecutingTransition,
  } = useAppSelector((state) => state.transitionRecord);

  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: classrooms } = useAppSelector((state) => state.classroom);

  // --- Local State ---
  const [step, setStep] = useState<number>(1); // 1 = Setup, 2 = Review/Execute
  const [localEvals, setLocalEvals] = useState<LocalEvaluation[]>([]);
  const [wizardPayload, setWizardPayload] = useState<WizardFormValues | null>(
    null,
  );

  // Sync evaluations to local state for editing streams
  useEffect(() => {
    if (evaluations && evaluations.length > 0) {
      const mapped = evaluations.map((ev) => ({
        ...ev,
        selectedClassroomId:
          ev.availableClassrooms?.length === 1
            ? ev.availableClassrooms[0].id
            : "",
      }));
      setLocalEvals(mapped);
      setStep(2);
    }
  }, [evaluations]);

  const handleClose = (): void => {
    setStep(1);
    setLocalEvals([]);
    onClose();
  };

  // --- Step 1: Handle Evaluate Trigger ---
  const handleEvaluate = (values: WizardFormValues): void => {
    setWizardPayload(values);

    const selectedClass = classrooms?.find((c) => c.id === values.classroomId);
    if (!selectedClass) return;

    const requestPayload: TransitionRecordEvaluationRequestDto = {
      fromAcademicYearId: values.fromAcademicYearId,
      toAcademicYearId: values.toAcademicYearId,
      specificClassroomId: values.classroomId,
      branchId: selectedClass.branchId || "",
      rules: [
        {
          gradeLevelIds: [selectedClass.gradeLevelId || ""],
          passMark: Number(values.passMark),
          evaluationMethod: "MARKS_AVERAGE",
          requireAllThreeTerms: Boolean(values.requireAllThreeTerms),
          isTerminalLevel: Boolean(values.isTerminalLevel),
        },
      ],
    };

    dispatch(evaluateTransitions(requestPayload));
  };

  // --- Step 2: Handle Stream/Status Changes ---
  const handleStatusChange = (
    enrollmentId: string,
    newStatus: TransitionStatus,
  ): void => {
    setLocalEvals((prev) =>
      prev.map((ev) =>
        ev.currentEnrollmentId === enrollmentId
          ? { ...ev, suggestedStatus: newStatus }
          : ev,
      ),
    );
  };

  const handleClassroomChange = (
    enrollmentId: string,
    classId: string,
  ): void => {
    setLocalEvals((prev) =>
      prev.map((ev) =>
        ev.currentEnrollmentId === enrollmentId
          ? { ...ev, selectedClassroomId: classId }
          : ev,
      ),
    );
  };

  // --- Step 3: Handle Final Execute ---
  const handleExecute = (): void => {
    if (!wizardPayload) return;

    const payload: TransitionRecordBulkRequestDto = {
      fromAcademicYearId: wizardPayload.fromAcademicYearId,
      toAcademicYearId: wizardPayload.toAcademicYearId,
      transitions: localEvals.map((ev) => ({
        studentId: ev.studentId,
        currentEnrollmentId: ev.currentEnrollmentId,
        status: ev.suggestedStatus as TransitionStatus,
        toGradeLevelId: ev.nextGradeLevelId || null,
        toClassroomId:
          ev.suggestedStatus === TransitionStatus.GRADUATED ||
          ev.suggestedStatus === TransitionStatus.TRANSFERRED_OUT
            ? null
            : ev.selectedClassroomId || null,
        comments: ev.remarks || null,
      })),
    };

    dispatch(executeBulkTransitions(payload));
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title="End of Year Transition Wizard"
      width="lg"
    >
      {/* STEP 1: CONFIGURATION */}
      {step === 1 && (
        <Formik
          initialValues={{
            fromAcademicYearId: "",
            toAcademicYearId: "",
            classroomId: "",
            passMark: 50.0,
            requireAllThreeTerms: true,
            isTerminalLevel: false,
          }}
          validationSchema={setupSchema}
          onSubmit={handleEvaluate}
        >
          {({ isValid }: FormikProps<WizardFormValues>) => (
            <Form>
              <Stack spacing={3}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Select the classroom you wish to process. The system will
                  calculate the yearly average based on Term 1, 2, and 3
                  reports.
                </Typography>

                {errorEvaluation && (
                  <Alert severity="error" sx={{ textAlign: "left" }}>
                    {errorEvaluation}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  <FormSelectField
                    name="fromAcademicYearId"
                    label="From Year (Current)"
                    options={
                      academicYears?.map((y) => ({
                        value: y.id,
                        label: y.name,
                      })) || []
                    }
                  />
                  <FormSelectField
                    name="toAcademicYearId"
                    label="To Year (Next)"
                    options={
                      academicYears?.map((y) => ({
                        value: y.id,
                        label: y.name,
                      })) || []
                    }
                  />
                  <FormSelectField
                    name="classroomId"
                    label="Classroom to Evaluate"
                    options={
                      classrooms?.map((c) => ({
                        value: c.id,
                        label: c.name,
                      })) || []
                    }
                  />
                </Box>

                <Divider />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Transition Rules
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  <FormTextField
                    name="passMark"
                    label="Pass Mark (e.g. 50)"
                    type="number"
                    slotProps={{ htmlInput: { step: "0.1", min: 0 } }}
                  />
                  <FormSelectField
                    name="requireAllThreeTerms"
                    label="Require All 3 Terms?"
                    options={[
                      { value: true as any, label: "Yes" },
                      { value: false as any, label: "No" },
                    ]}
                  />
                  <FormSelectField
                    name="isTerminalLevel"
                    label="Is Graduation Class?"
                    options={[
                      { value: false as any, label: "No (Promote)" },
                      { value: true as any, label: "Yes (Graduate)" },
                    ]}
                  />
                </Box>

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                  <Button
                    onClick={handleClose}
                    color="inherit"
                    disabled={loadingEvaluation}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isValid || loadingEvaluation}
                    color="primary"
                  >
                    {loadingEvaluation
                      ? "Analyzing Data..."
                      : "Evaluate Classroom"}
                  </Button>
                </Box>
              </Stack>
            </Form>
          )}
        </Formik>
      )}

      {/* STEP 2: REVIEW AND EXECUTE */}
      {step === 2 && (
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Review System Suggestions
          </Typography>

          {errorExecutingTransition && (
            <Alert severity="error" sx={{ textAlign: "left" }}>
              {errorExecutingTransition}
            </Alert>
          )}

          <TableContainer
            sx={{
              maxHeight: 500,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Yearly Avg</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>System Remark</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Target Stream (Next Year)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localEvals.map((ev) => {
                  const isExiting =
                    ev.suggestedStatus === TransitionStatus.GRADUATED ||
                    ev.suggestedStatus === TransitionStatus.TRANSFERRED_OUT;

                  return (
                    <TableRow key={ev.currentEnrollmentId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {ev.studentName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {ev.admissionNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {ev.yearlyAverage}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            color: ev.isEligible
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          {ev.remarks}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={ev.suggestedStatus || ""}
                          onChange={(e) =>
                            handleStatusChange(
                              ev.currentEnrollmentId,
                              e.target.value as TransitionStatus,
                            )
                          }
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value={TransitionStatus.PROMOTED}>
                            Promoted
                          </MenuItem>
                          <MenuItem value={TransitionStatus.REPEATED}>
                            Repeated
                          </MenuItem>
                          <MenuItem value={TransitionStatus.GRADUATED}>
                            Graduated
                          </MenuItem>
                          <MenuItem value={TransitionStatus.TRANSFERRED_OUT}>
                            Transferred Out
                          </MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={ev.selectedClassroomId || ""}
                          onChange={(e) =>
                            handleClassroomChange(
                              ev.currentEnrollmentId,
                              e.target.value,
                            )
                          }
                          disabled={isExiting}
                          error={!isExiting && !ev.selectedClassroomId}
                          sx={{ minWidth: 160 }}
                        >
                          <MenuItem value="" disabled>
                            -- Select Stream --
                          </MenuItem>
                          {ev.availableClassrooms?.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              onClick={() => setStep(1)}
              color="inherit"
              disabled={loadingTransitionExecute}
            >
              Back to Setup
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExecute}
              disabled={
                loadingTransitionExecute ||
                localEvals.some(
                  (ev) =>
                    (ev.suggestedStatus === TransitionStatus.PROMOTED ||
                      ev.suggestedStatus === TransitionStatus.REPEATED) &&
                    !ev.selectedClassroomId,
                )
              }
            >
              {loadingTransitionExecute
                ? "Processing Moves..."
                : "Confirm & Execute Transitions"}
            </Button>
          </Box>
        </Stack>
      )}
    </AppModal>
  );
};

export default TransitionWizardModal;
